import { Box, Typography } from "@mui/material";
import { MicrosoftInsightsDialog } from "./MicrosoftInsightsDialog";
import overageExample from "../assets/overage-example.png";
import underutilizationExample from "../assets/underutilization-example.png";

/** Structure of the props that dialog box accepts. */
interface MicrosoftInsightsHowToUseProps {
  /** Action performed to close the dialog. */
  onClose: () => void;
  /** Flag that controls whether the dialog is open or closed. */
  open: boolean;
}


/**
 * Creates a dialog component that provides instructions on how to use the Microsoft Insights report.
 * @param props Configuration options to manage the dialog's open state and handle closing actions.
 * @returns A dialog component with instructional content on using the report.
 */
export function MicrosoftInsightsHowToUse(props: MicrosoftInsightsHowToUseProps): React.ReactNode {
  return (
    <MicrosoftInsightsDialog open={props.open} onClose={props.onClose} title="How to Use This Report">
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "0.0625rem solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Top Level Metrics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Three key metrics for the most common Microsoft subscriptions (e.g., Microsoft 365 E3).
          These metrics are tailored to your environment and help you quickly understand your
          licenses usage:
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <ul>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>MAU (Monthly Active Users)</Typography> - The number of people actively using the service each month.
            </li>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>Assigned</Typography> - The number of licenses that have been assigned to users in your organization.
            </li>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>Purchased</Typography> - The total number of licenses your organization purchased. This is seen as a vertical bar marker on the chart.
            </li>
          </ul>
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "0.0625rem solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Filtering
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Use the <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>View Usage By</Typography> filter to select a specific license group, to see which features are available for each group, and quickly spot areas in your environment that need attention.
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "0.0625rem solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Read Each Capability Row
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary">
          Each capability row displays three metrics:
          <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> MAU, In Scope,</Typography> and
          <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> Purchased.</Typography> Since
          <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> MAU</Typography> and
          <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> Purchased</Typography> have already been defined above, let's look at the last metric:
          <ul>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>In Scope</Typography> - Users who are eligible to use that service.
            </li>
          </ul>
          There are two patterns to look for:
          <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> High Risk</Typography> &
          <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> Low Utilization.</Typography>
          <ul>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>High Risk</Typography> - Marked with red warning indicators. This means the number of active users
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> (MAU)</Typography> or eligible users
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> (In Scope)</Typography> exceed the number of
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> Purchased</Typography> licenses. This could put your organization at risk for compliance issues.
              <Box
                component="img"
                src={overageExample}
                alt="Example of a high risk overage feature row"
                loading="lazy"
                sx={{ display: "block", maxWidth: "100%", mt: 1 }}
              />
            </li>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>Low Utilization</Typography> - There are no warning indicators for low utilization, but it's still important to keep an eye on. This happens when the number of active users
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> (MAU)</Typography> or eligible users
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> (In Scope)</Typography> are significantly lower than the number of
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}> Purchased</Typography> licenses, which could mean:
              <ul>
                <li>
                  Users are not adopting the feature
                </li>
                <li>
                  Too many licenses may have been purchased
                </li>
              </ul>
              <Box
                component="img"
                src={underutilizationExample}
                alt="Example of a low utilization feature row"
                loading="lazy"
                sx={{ display: "block", maxWidth: "100%", mt: 1 }}
              />
            </li>
          </ul>
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "0.0625rem solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Expand Rows for More Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          To get more information about each feature or capability in your report, click on the row
          to expand it. Once a row is expanded, you'll see a detailed breakdown of the selected
          feature, organized by license type. This detailed breakdown helps you understand exactly
          what assigned license group is utilizing the service plan and where there might be issues
          or opportunities for optimization.
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary">
          There are also badges that may appear next to certain license types, which highlight important information:
          <ol>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>Unlicensed</Typography> - Represents users that qualify for usage but there are not enough licenses assigned to cover them.
            </li>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>Add-On</Typography> - Shows where the extra licenses are covering usage, beyond the standard purchased licenses.
            </li>
          </ol>
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "0.0625rem solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 2
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Why This Matters & What to Do
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This report helps you gain insight into your usage compared to your ownership and highlights potential risks.
          <ul>
            <li>
              Identify and resolve licensing gaps before they become bigger issues.
            </li>
            <li>
              Understand the feature gap between license capability tiers.
            </li>
            <li>
              Make informed decisions about license purchases.
            </li>
            <li>
              <Typography component="span" fontSize="0.875rem" sx={{ fontWeight: 600 }}>Actions you can take:</Typography>
              <ul>
                <li>
                  Discuss license allocation and possible optimization areas.
                </li>
                <li>
                  Review avenues to drive further adoption.
                </li>
              </ul>
            </li>
          </ul>
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          border: "0.0625rem solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Quick Tips
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <ul>
            <li>
              Expand rows to see what areas need attention.
            </li>
            <li>
              Be mindful of red warnings which signal urgent issues.
            </li>
            <li>
              Review low usage to optimize your license purchases.
            </li>
            <li>
              Review this data with your SHI Customer Success Manager.
            </li>
          </ul>
        </Typography>
      </Box>
    </MicrosoftInsightsDialog>
  );
}

